import type React from 'react';
import { type ComponentProps, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';

import useConfig from 'app/config';

import HyroxLogo from 'src/app/ui/components/atoms/icons/hyrox-logo';

import ProfileIcon from 'app/assets/profile-icon.svg';
import { routes } from 'app/routes/constants';
import { Link } from 'react-router-dom';

import LanguagePicker from 'app/ui/components/molecules/picker/language/wrapper';
import { useGymStructure } from 'src/app/providers/gym-structure';
import HpcLogo from 'app/ui/components/atoms/hpc-logo';
import { GymStructure } from 'src/types/graphql';

type Path = {
  pathname: string;
  search?: string;
};

type MenuItem = {
  path: Path;
  localeKey: string;
};

const NavbarContainer = styled.nav`
  z-index: 99;
  position: sticky;
  top: 0;
  width: 100%;

  ${({ theme: { colors, typography, spacing } }) => `
    font-size:  ${typography.small};
    background: ${colors.black};
    color: ${colors.white};
    margin-bottom: ${spacing(4)};
  `}
`;

const NavbarContentWrapper = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
  height: 4.75rem;
  justify-content: space-between;
  margin: 0 auto;

  ${({ theme: { media } }) => `
    ${media.small(
      () => `
        padding: 0 2.1875rem;
      `,
    )}
    ${media.large(
      () => `
        padding: 0 2.1875rem;
        max-width: 67.5rem;
      `,
    )}
  `}
`;

const LogoWrapper = styled.div<{ width?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    width: 6.5rem;
  }

  ${({ theme: { media }, width }) => `
    ${media.large(
      () => `
        svg {
          width: ${width || '7.625rem'};
        }
      `,
    )}
  `}
`;

const Menu = styled.ul<{ $isMenuOpen: boolean }>`
  ${({ theme: { media, colors }, $isMenuOpen }) => `
  height: fit-content;
  list-style: none;
  text-decoration: none;
  gap: 1.875rem;
  color: ${colors.white};
  background: ${colors.black};
  position: absolute;
  display: ${$isMenuOpen ? 'flex' : 'none'};
  top: 4.5rem;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  flex-direction: column;
  text-align: center;
  padding-top: 0.5rem;

  ${media.large(
    () => `
      position: relative;
      width: fit-content;
      height: auto;
      top: unset;
      left: unset;
      right: unset;
      bottom: unset;
      display: flex;
      flex-direction: row;
      align-items: baseline;
      margin-left: auto;
      padding-top: unset;
    `,
  )}

  `}
`;

const NavMenuItem = styled.li`
  cursor: pointer;
  border-bottom: 0.0625rem solid transparent;
  padding-bottom: ${({ theme: { spacing } }) => spacing(3)};

  a {
    text-decoration: none;
    color: inherit;
  }
  ${({ theme: { colors, media } }) => `
    ${media.large(
      () => `
      transition: border-bottom 0.3s ease-in-out;
      padding-bottom: 0;
      &:hover {
        border-bottom: 0.0625rem solid ${colors.focoltone};
      }`,
    )}
  `}
`;

const ProfileButton = styled(Link)`
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 1rem;
  margin-left: auto;
  ${({ theme: { media } }) => `
    ${media.large(
      () => `
        margin-left: 0;
      `,
    )}`}
`;

// only for mobile
const BurgerMenu = styled.div`
  display: block;

  ${({ theme: { media } }) => `
  ${media.large(
    () => `
    display: none;
    cursor: pointer;`,
  )}

  
`}
`;

const ProfileIconWrapper = styled.div`
  width: 2rem;
  height: 2rem;
  align-items: baseline;

  img {
    width: 100%;
    height: 100%;
  }
`;

// only for mobile
const BurgerLine = styled.span`
  display: block;
  width: 1.5625rem;
  height: 0.1875rem;
  margin: 0.3125rem 0;
  transition: all 0.3s ease-in-out;
  ${({ theme: { colors } }) => `
    background: ${colors.white};
  `}
`;

const UserSettingsArea = styled.div`
  display: flex;
  gap: ${({ theme: { spacing } }) => spacing(2)};

  ${({ theme: { media, spacing } }) => `
    ${media.large(
      () => `
        margin-left: ${spacing(8)};
      `,
    )}
  `}
`;

const menuItems: MenuItem[] = [
  { path: { pathname: '/' }, localeKey: 'home' },
  { path: { pathname: routes.LIBRARY, search: '?fromNav' }, localeKey: 'library' },
  { path: { pathname: routes.RESOURCES }, localeKey: 'resources' },
  { path: { pathname: routes.FAQ }, localeKey: 'support' },
];

const profilePath = routes.PROFILE;

const Navbar: React.FC<ComponentProps<'nav'>> = ({ className }) => {
  const intl = useIntl();
  const { config } = useConfig();
  const { gymStructure } = useGymStructure();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const renderLogo = useCallback(() => {
    if (gymStructure === GymStructure.HPC) {
      return (
        <LogoWrapper width="20rem">
          <HpcLogo />
        </LogoWrapper>
      );
    }
    return (
      <LogoWrapper>
        <HyroxLogo />
      </LogoWrapper>
    );
  }, [gymStructure]);

  return (
    <NavbarContainer className={className}>
      <NavbarContentWrapper>
        <BurgerMenu onClick={toggleMenu}>
          <BurgerLine />
          <BurgerLine />
          <BurgerLine />
        </BurgerMenu>
        <Link to="/">{renderLogo()}</Link>
        <Menu $isMenuOpen={isMenuOpen}>
          {menuItems
            .filter(({ path }) => (config.REACT_APP_NEW_LIBRARY_ENABLED ? true : path.pathname !== routes.LIBRARY))
            .map(({ path, localeKey }) => (
              <NavMenuItem key={localeKey}>
                <Link to={path}>{intl.formatMessage({ id: `nav.${localeKey}` })}</Link>
              </NavMenuItem>
            ))}
        </Menu>
        <UserSettingsArea>
          {config.REACT_APP_LOCALISATION_ENABLED && <LanguagePicker />}
          <ProfileButton to={profilePath}>
            <ProfileIconWrapper>
              <img src={ProfileIcon} alt="Link to profile page" />
            </ProfileIconWrapper>
          </ProfileButton>
        </UserSettingsArea>
      </NavbarContentWrapper>
    </NavbarContainer>
  );
};

export default Navbar;
